import React, { useContext, useMemo } from 'react';
import { Link, LinkProps, NavLink, useHistory } from 'react-router-dom';
import { Match } from 'path-to-regexp';

import { isAbsolutePath } from '../util/isAbsolutePath';
import { IMatchType, IRoutesContextType, RoutesContext } from './RoutesWrapper';
import { flushSync } from 'react-dom';

// this type should be expanded to handle all the use cases available on the Link component
type ITo = string;
export function convertToLinkValue(routes: IRoutesContextType, to: ITo): ITo {
  if (!routes) return to;
  if (typeof to !== 'string') return to;

  const [path, ...queries] = to.split('?');
  const queryValue = queries.join('?');
  const [toValue, ...hashes] = path.split('#');
  const hashValue = hashes.join('#');

  let matchResult: Match<IMatchType>;
  const matchRoute = Object.keys(routes)
    .map(key => routes[key])
    .find(routeObj => {
      matchResult = routeObj.match?.(toValue);
      return matchResult;
    });

  if (!matchRoute || !matchResult) {
    return to;
  }

  let convertedValue = matchRoute.toPath(matchResult.params);
  if (hashValue) convertedValue = `${convertedValue}#${hashValue}`;
  if (queryValue) convertedValue = `${convertedValue}?${queryValue}`;

  return convertedValue;
}

interface IABLinkProps extends LinkProps {
  to: ITo;
  children?: React.ReactNode;
  isNavLink?: boolean;
  onAfterHistoryPush?: () => void;
}

const ABLink = React.forwardRef<HTMLAnchorElement>(
  ({ to, isNavLink, onAfterHistoryPush, ...props }: IABLinkProps, ref) => {
    const routes = useContext(RoutesContext);
    const history = useHistory();
    const newTo = useMemo<string>(() => convertToLinkValue(routes, to), [routes, to]);

    if (isAbsolutePath(newTo)) {
      return <a ref={ref} href={newTo} {...props} />;
    }

    return isNavLink ? (
      <NavLink ref={ref} to={newTo} {...props} />
    ) : (
      <Link
        ref={ref}
        to={newTo}
        {...props}
        onClick={
          onAfterHistoryPush
            ? e => {
                props.onClick?.(e);
                if (!e.defaultPrevented && shouldProcessLinkClick(e)) {
                  e.preventDefault();
                  flushSync(() => {
                    history.push(newTo);
                  });
                  onAfterHistoryPush();
                }
              }
            : props.onClick
        }
      />
    );
  },
);

/**
 * Checks if the link click should be intercepted and history.push should be used
 *
 * @note Taken from https://github.com/remix-run/react-router/blob/main/packages/react-router/lib/dom/dom.ts#L34-L43
 */
function shouldProcessLinkClick(
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  target?: string,
) {
  return (
    event.button === 0 && // Ignore everything but left clicks
    (!target || target === '_self') && // Let browser handle "target=_blank" etc.
    !isModifiedEvent(event) // Ignore clicks with modifier keys
  );
}

function isModifiedEvent(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

export default ABLink;
